import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AnalyticsPixel from "@/components/AnalyticsPixel";
import { ArrowRight } from "lucide-react";

/**
 * Reusable shell for static content pages (comment ça marche, mentions légales,
 * confidentialité, assistance…). Consistent layout, breadcrumb and CTA.
 */
export default function LegalShell({ overline, title, subtitle, children, cta }) {
    return (
        <div>
            <AnalyticsPixel />
            <Header />
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
                <nav
                    className="text-xs text-slate-400 mb-4"
                    aria-label="Fil d'ariane"
                    data-testid="legal-breadcrumb"
                >
                    <Link to="/" className="hover:text-[#F5A623]">Accueil</Link>
                    <span className="mx-2">/</span>
                    <span>{overline}</span>
                </nav>
                {overline && <div className="overline mb-2">{overline}</div>}
                <h1
                    className="font-display text-4xl md:text-5xl text-[#0B192C] tracking-tight mb-4"
                    data-testid="legal-title"
                >
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-lg text-slate-500 mb-10 leading-relaxed max-w-3xl">
                        {subtitle}
                    </p>
                )}
                <article className="sd-card p-8 md:p-12 prose prose-slate max-w-none">
                    {children}
                </article>
                {cta && (
                    <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#F5A623] to-[#f7b74a] p-8 md:p-10">
                        <div className="grid md:grid-cols-2 gap-4 items-center">
                            <div>
                                <div className="font-display text-2xl text-[#0B192C] mb-2">
                                    {cta.title}
                                </div>
                                <p className="text-sm text-[#0B192C]/80">{cta.text}</p>
                            </div>
                            <div className="md:text-right">
                                <Link
                                    to={cta.to}
                                    className="btn-navy inline-block"
                                    data-testid="legal-cta"
                                >
                                    {cta.label}
                                    <ArrowRight className="inline ml-2 -mr-1" size={16} />
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

/** Utility for consistent section styling inside LegalShell children */
export function Section({ title, id, children }) {
    return (
        <section className="mb-8" id={id} data-testid={id ? `legal-section-${id}` : undefined}>
            <h2 className="font-display text-2xl text-[#0B192C] mt-0 mb-3">{title}</h2>
            <div className="text-slate-600 leading-relaxed text-[15px] space-y-3">
                {children}
            </div>
        </section>
    );
}
